import React from 'react';

// --- Helper Component: Price Display ---
const PriceDisplay = ({ price, unit }) => {
    if (typeof price !== 'number') {
        return <span className="item-price text-muted">N/A</span>;
    }
    const formattedPrice = price.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

    // Simplified unit text generation
    const unitText = unit ? ` /${unit.replace(/per_?/i, '').trim()}` : '';

    return (
        <span className="item-price">
            {formattedPrice}
            {unitText && <span className="small text-muted">{unitText}</span>}
        </span>
    );
};

// --- Helper Component: Quantity Input ---
const QuantityInput = ({ fieldName, quantity, unit, disabled, onChange }) => (
    <div className="d-flex align-items-center" style={{ width: "flex" }}>
        <label htmlFor={`${fieldName}_qty`} className="form-label small me-2 mb-0 text-uppercase">{unit || 'Qty'}:</label>
        <input
            type="number"
            id={`${fieldName}_qty`}
            className="item-box form-control form-control-sm text-center"
            value={quantity || ''}
            onChange={onChange}
            disabled={disabled}
            min="1"
            placeholder="1"
        />
    </div>
);

// --- Helper Component: Total Display ---
const TotalDisplay = ({ total }) => {
    const formattedTotal = (total || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    return (
        <span className="item-total" title={formattedTotal}>
            {formattedTotal}
        </span>
    );
};
// --- Helper Component: Checkbox ---
const Checkbox = ({ fieldName, checked, onChange, disabled }) => (
    <div className="form-check">
        <input
            type="checkbox"
            id={`${fieldName}_chk`}
            className="form-check-input"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
        />
    </div>
);


const FormField = ({ field, value, onFormChange, error, isReadOnly = false }) => {

    if (!field.fieldType) {
        return null;
     }
    const isFieldDisabled = isReadOnly || field.isAutoFilled;

    // Special business rule for unavailable basement options
    const isBasementNotAvailable = (
        (field.fieldName === 'UnfinishedBasement' || field.fieldName === 'finishedBasement') &&
        (field.price === 0 || field.price === null)
    );

    if (isBasementNotAvailable) {
        return (
            <div className="item-not-available selection-item-row">
                <div className="item-id-column text-muted">{field.itemId}</div>
                <span className="fw-bold">{field.label}</span>
                <span>Not available for this lot</span>
            </div>
        );
    }

    switch (field.fieldType) {
        case 'text':
        case 'email':
        case 'tel':
        case 'date': {
            return (
                <div>
                    <label htmlFor={field.fieldName} className="form-label mb-1">{field.label} {field.isRequired && !isFieldDisabled && <span className="text-danger">*</span>}</label>
                    <input
                        type={field.fieldType}
                        id={field.fieldName}
                        name={field.fieldName}
                        className={`form-control ${error ? 'is-invalid' : ''}`}
                        value={value || ''}
                        onChange={(e) => onFormChange(field.fieldName, e.target.value)}
                        required={field.isRequired}
                        disabled={isFieldDisabled}
                    />
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </div>
            );
        }

        case 'select': {
            return (
                <div>
                    <label htmlFor={field.fieldName} className="form-label mb-1">{field.label} {field.isRequired && <span className="text-danger">*</span>}</label>
                    <select id={field.fieldName} name={field.fieldName} className={`form-select ${error ? 'is-invalid' : ''}`} value={value || ''} onChange={(e) => onFormChange(field.fieldName, e.target.value)} required={field.isRequired} disabled={isFieldDisabled}>
                       <option value="">-- Please Select --</option>
                    </select>
                    {error && <div className="invalid-feedback d-block">{error}</div>}
                </div>
            );
        }

        case 'display_date': {
             return (
                 <div className="p-2 d-flex justify-content-between align-items-center">
                    <span className="form-label text-muted">{field.label}:</span>
                    <span className="fw-bold ms-2">{value ? new Date(value).toLocaleDateString('en-US', { timeZone: 'UTC' }) : 'N/A'}</span>
                 </div>
             );
        }

        case 'display_static': {
            return (
                <div className="selection-item-row">
                    <div className="item-id-column text-muted">{field.itemId}</div>
                    <div className="item-details">
                        <span className="fw-bold">{field.label}</span>
                        <p className="small text-muted mb-0">{field.description}</p>
                    </div>
                    <PriceDisplay price={field.price} unit={field.unit} />
                </div>
            );
        }

        case 'manual_entry':
        case 'upgrade_item': {
             // Destructure with defaults to prevent errors
             const { selected = false, quantity = 1, manualPrice = 0, comment = '' } = value || {};

             // console.log(`Rendering item: ${field.itemId}, Label: ${field.label}, FieldType: ${field.fieldType}`); // KEEP THIS FOR DIAGNOSIS IF NEEDED

             const isUpgrade = field.fieldType === 'upgrade_item';

             // --- START OF MODIFICATIONS FOR OEU04 ---
             const isOEU04CreditField = field.itemId === 'OEU04';

             // UNIFIED LOGIC: Any upgrade with a unit needs a quantity input.
             const needsQuantityInput = isUpgrade && field.unit;

             // The quantity to use in calculations is either the entered quantity or 1 as a default
             const calculationQuantity = needsQuantityInput ? (Number(quantity) || 1) : 1;

             // Calculation for rowTotal - ensure OEU04 uses its manualPrice if selected
             const rowTotal = selected
                 ? (isUpgrade && !isOEU04CreditField) // If it's a regular upgrade and NOT OEU04
                     ? (field.price || 0) * calculationQuantity
                     : parseFloat(manualPrice) // Otherwise (manual_entry or OEU04), use manualPrice
                 : 0;

             // Constants for the input's min and placeholder
             const inputMin = isOEU04CreditField ? -Infinity : 0; // OEU04 allows negative, others non-negative
             const inputPlaceholder = isOEU04CreditField ? '-100' : '0'; // Hint for OEU04

             // --- NEW: Handle change for credit field to force negative values ---
             const handlePriceChange = (e) => {
                 let newValue = parseFloat(e.target.value);
                 if (Number.isNaN(newValue)) {
                     newValue = ''; // Allow clearing input
                 } else if (isOEU04CreditField && newValue > 0) {
                     // If it's the credit field and a positive value is entered,
                     // force it to be negative by multiplying by -1, or set to 0 if 0 was intended.
                     newValue = -Math.abs(newValue);
                     if (parseFloat(e.target.value) === 0) { // If user explicitly typed '0'
                         newValue = 0;
                     }
                 }
                 onFormChange(field.fieldName, { ...value, manualPrice: newValue });
             };
             // --- END OF MODIFICATIONS FOR OEU04 ---


             return (
                 <div className="selection-item-row flex-wrap">
                    <div className="item-id-column text-muted">{field.itemId}</div>
                    <div className="item-details">
                        <label className="form-check-label fw-bold" htmlFor={`${field.fieldName}_chk`}>{field.label}</label>
                        <p className="small text-muted mb-0">{field.description}</p>
                    </div>
                    <div className="item-controls">
                        {/* If it's a regular upgrade, show PriceDisplay. If it's OEU04 or manual_entry, show input box */}
                        {isUpgrade && !isOEU04CreditField ? (
                            <PriceDisplay price={field.price} unit={field.unit} />
                        ) : (
                            <div className="input-group input-group-sm" style={{width: '130px'}}>
                               <input
                                   type="number"
                                   className="form-control text-end"
                                   value={manualPrice}
                                   onChange={handlePriceChange} // MODIFIED: Use the new handler
                                   disabled={!selected || isFieldDisabled}
                                   style={{width:"85px"}}
                                   min={inputMin}         // Apply the calculated min (browser validation)
                                   placeholder={inputPlaceholder} // Apply the calculated placeholder
                               />
                            </div>
                        )}

                        {needsQuantityInput ? (
                            <QuantityInput
                                fieldName={field.fieldName}
                                quantity={quantity}
                                unit={field.unit.replace(/per_?/i, '').trim()}
                                disabled={!selected || isFieldDisabled}
                                onChange={(e) => {
                                    const newQuantity = e.target.value;
                                    onFormChange(field.fieldName, { ...value, quantity: newQuantity });
                                }}
                            />
                        ) : (
                            <div style={{width: '120px'}}></div> // Placeholder to maintain alignment
                        )}
                        <TotalDisplay total={parseFloat(rowTotal)} />
                        <Checkbox fieldName={field.fieldName} checked={selected} onChange={(e) => onFormChange(field.fieldName, { ...value, selected: e.target.checked })} disabled={isFieldDisabled} />
                    </div>
                    {selected && (
                        <div className="w-100 mt-2 ps-5">
                            <textarea
                                className="form-control form-control-sm"
                                placeholder="Add a comment... (optional)"
                                value={comment || ''}
                                onChange={(e) => onFormChange(field.fieldName, { ...value, comment: e.target.value })}
                                disabled={isFieldDisabled}
                                rows="1"
                            ></textarea>
                        </div>
                    )}
                </div>
             );
        }

        case 'text_area': {
            return (
                <div>
                  <label htmlFor={field.fieldName} className="form-label mb-1">{field.label}</label>
                  <textarea
                    id={field.fieldName}
                    className={`form-control ${error ? 'is-invalid' : ''}`}
                    rows="4"
                    value={value || ''}
                    onChange={(e) => onFormChange(field.fieldName, e.target.value)}
                    disabled={isFieldDisabled}
                  />
                  {error && <div className="invalid-feedback d-block">{error}</div>}
                </div>
            );
        }

        default:
            return <div className="alert alert-warning m-2">Unsupported Field Type: {field.fieldType}</div>;
    }
};
export default FormField;